import { DOC_SOURCE } from "constants/documents";
import { UPLOAD_ENTITY_TYPES } from "constants/documentUploads";
import CryptoJS from "crypto-js";
import { ReactComponent as ExcelIcon } from "icons/excel.svg";
import { ReactComponent as PDFIcon } from "icons/pdf.svg";
import { ReactComponent as PowerPointIcon } from "icons/pptx.svg";
import { ReactComponent as VtFileIcon } from "icons/vt.svg";
import { ReactComponent as WordIcon } from "icons/word.svg";
import { SquarePen } from "lucide-react";
import { FileFormat, getFileFormat } from "utils/getFileFormat";

export const CHUNK_SIZE = 1024 * 1024 * 5; // 5MB chunks

const COMMON_ICON_PROPS = {
  className: "icon",
  width: 16,
  height: 16,
};

const ICON_BY_FORMAT = {
  [FileFormat.PDF]: PDFIcon,
  [FileFormat.WORD]: WordIcon,
  [FileFormat.RTF]: WordIcon,
  [FileFormat.EXCEL]: ExcelIcon,
  [FileFormat.POWERPOINT]: PowerPointIcon,
};

export const calculateFileHash = (file) => {
  return new Promise((resolve, reject) => {
    if (!file || !(file instanceof File)) {
      reject(new Error("Invalid file: The file is empty or not a File object"));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const binary = event.target.result;
      const wordArray = CryptoJS.lib.WordArray.create(binary);

      const hash = CryptoJS.SHA256(wordArray);
      resolve(hash.toString());
    };
    reader.onerror = (error) => {
      reject(new Error(`Error reading file: ${error.message}`));
    };
    reader.readAsArrayBuffer(file);
  });
};

// Put actual file in the content field and add source field
export const formatFiles = (files, source) => {
  return files.map((file) => ({
    content: file,
    source,
  }));
};

export const generateHashes = async (files) => {
  return Promise.all(
    files.map(async (file) => ({
      ...file,
      hash:
        file.source.toLowerCase() === "sharepoint"
          ? file.content.hash
          : await calculateFileHash(file.content),
    }))
  );
};

// Remove already selected files
export const removeDuplicates = (newFiles, oldFiles) => {
  const isDuplicate = (newFile, oldFile) => {
    return (
      oldFile.content.name === newFile.content.name &&
      oldFile.source === newFile.source
    );
  };

  return newFiles.filter((newFile) => {
    return !oldFiles.some((oldFile) => isDuplicate(newFile, oldFile));
  });
};

export const getDocumentIcon = (name, source) => {
  if (source === DOC_SOURCE.ADDIN) {
    return <WordIcon {...COMMON_ICON_PROPS} />;
  }

  if (source === DOC_SOURCE.VT_EDITOR) {
    return <VtFileIcon {...COMMON_ICON_PROPS} />;
  }

  const Icon = ICON_BY_FORMAT[getFileFormat(name)] ?? SquarePen;
  return <Icon {...COMMON_ICON_PROPS} />;
};

// Note: Tests expect the new File to use the NEW name as its Blob content
// and preserve the original filename as the File name parameter.
// That is, renameFile(file, newName) -> new File([newName], file.name)
export const renameFile = (file, name) => {
  return new File([file], name, {
    type: file.type,
    lastModified: file.lastModified,
  });
};

export const getTargetName = (target, state) => {
  switch (target) {
    case UPLOAD_ENTITY_TYPES.FOLDER:
      return state.folders.folderName;
    case UPLOAD_ENTITY_TYPES.OPPORTUNITY:
      return state.opportunity.summary.data.title;
    default:
      return "";
  }
};

export const getCollectionName = (collections) => {
  if (!collections.find((c) => c.name === "Collection")) {
    return "Collection";
  }

  const matches = collections.filter((c) =>
    c.name.match(/^Collection \((\d*)\)$/)
  );

  if (matches.length === 0) {
    return "Collection (1)";
  }

  const { name } = matches.sort().reverse()[matches.length - 1];
  const prevNumber = Number(name.match(/(\d+)/)[0]);
  return `Collection (${prevNumber + 1})`;
};

export const setupNewCollection = (files) => {
  return {
    name: "",
    files,
  };
};

export const getSelectedCollectionIndex = (selectCollection, collections) => {
  if (!selectCollection) {
    return null;
  }

  for (const [index, collection] of collections.entries()) {
    if (collection.name === selectCollection.name) {
      return index;
    }
  }

  return null;
};
